// Live Dashboard. GET /api/v1/portfolio/summary[?client_id=<uuid>].
//   - role=client: server auto-scopes to linked client; we omit client_id.
//   - role=manager/auditor: we must pass ?client_id=. If missing → redirect
//     to /clients so the user can pick one. If the API returns 404, same.
//   - role=client with no linkage: 409 → show a helpful empty state.

/* eslint-disable @typescript-eslint/no-explicit-any */
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { AssetMark, Banner, DollarStat, Kicker, KickerRow, Stat, TypePill } from '../../components/shell';
import { fmtPct, fmtQty, fmtUSD, fmtUSDcompact } from '../../lib/format';
import { api } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { usePreferences } from '../../lib/preferences';
import { usePolling } from '../../lib/usePolling';

const DASHBOARD_POLL_MS = 60_000;

// Tiny asset-name lookup. The server returns tickers; pretty names are pure UI.
const ASSET_NAMES: Record<string, string> = {
  BTC: 'Bitcoin', ETH: 'Ether', SOL: 'Solana', ARB: 'Arbitrum',
  MATIC: 'Polygon', USDC: 'USD Coin', USDT: 'Tether', USD: 'US Dollar',
};

interface DashboardPayload {
  client: { id: string; legal_name: string; short_name: string; account_opened: string; accounts: any[] };
  period: { label: string; short_label: string; status: string; as_of: string | null; closes: string; statement_available: string | null } | null;
  totals: {
    aum: number; aum_prev: number; mtd_pct: number; ytd_pct: number; itd_pct: number;
    btc_itd_pct: number; btc_mtd_pct: number; benchmark_available: boolean;
    cost_basis: number; unrealized: number;
    data_age_seconds: number | null;
    staleness_warn_seconds: number;
  };
  pending_deposit: { amount: number; asset: string; applies_on: string; source: string; received: string } | null;
  holdings: { asset: string; qty: number; cost_basis: number; value: number; price: number }[];
  activity: { occurred_at: string; tx_type: string; asset: string; quantity: number; price_usd: number | null; usd_value: number; venue_tx_id: string; account_id: string }[];
  monthly: { month: string; opening: number; ending: number; flows: number; ret: number }[];
  last_statement: { id: string; period_label: string; version: number; issued_at: string | null } | null;
}

export default function Dashboard() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { user } = useAuth();
  const { appearance } = usePreferences();
  const clean = appearance === 'clean';
  const clientId = params.get('client_id');

  const [unlinked, setUnlinked] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  // Phase 9.b — Fund-position pointer. Dashboard is SMA-shaped (built
  // pre-Phase-8); a Fund-only investor lands here, sees an empty hero,
  // and has no idea their Fund position lives on /fund. One round-trip
  // to /portfolio/fund tells us whether they hold any fund units; the
  // banner renders only when they do. Cheap because /portfolio/fund
  // returns 204 quickly when there are no positions.
  const [fundPositionsCount, setFundPositionsCount] = useState(0);

  // Manager-impersonation context: when navigating to other portal pages
  // we have to forward `?client_id=` or the destination page sees a
  // manager with no client picked and bounces back to /clients. The shell
  // Nav already does this for top-level nav links; the Dashboard's inline
  // buttons (`navigate('/holdings')` etc.) need their own helper.
  const goto = (path: string) =>
    navigate(clientId ? `${path}?client_id=${clientId}` : path);

  // Managers without ?client_id= → /fund (their fund-first home). Run as an
  // effect so the navigation also fires on later URL changes, not just on mount.
  useEffect(() => {
    if (user && user.role !== 'client' && !clientId) {
      navigate('/fund', { replace: true });
    }
  }, [user, clientId, navigate]);

  const summaryUrl = '/portfolio/summary' + (clientId ? `?client_id=${clientId}` : '');
  const refreshUrl = '/portfolio/refresh' + (clientId ? `?client_id=${clientId}` : '');
  const pollingEnabled = !!user && (user.role === 'client' || !!clientId);
  const { data, error, refresh } = usePolling<DashboardPayload>(
    (signal) => api<DashboardPayload>(summaryUrl, { signal }),
    { intervalMs: DASHBOARD_POLL_MS, deps: [summaryUrl], enabled: pollingEnabled },
  );

  // On mount / when the target client changes, hit POST /portfolio/refresh so
  // the server pulls a live snapshot from Coinbase before we render. Then
  // re-fetch the summary to surface the new numbers. Fire-and-forget on the
  // refresh: if it fails (rate-limited, key issue, etc.) we still show the
  // last cached snapshot — partial degraded > broken page.
  useEffect(() => {
    if (!pollingEnabled) return;
    let cancelled = false;
    api(refreshUrl, { method: 'POST' })
      .catch(() => { /* surfaced via the next polling tick if persistent */ })
      .finally(() => { if (!cancelled) refresh(); });
    return () => { cancelled = true; };
  }, [refreshUrl, pollingEnabled, refresh]);

  // Phase 9.b — fetch fund positions once. Used to render the
  // "View your fund position →" banner when fundPositionsCount > 0.
  useEffect(() => {
    if (!pollingEnabled) return;
    const url = '/portfolio/fund' + (clientId ? `?client_id=${clientId}` : '');
    api<{ positions: any[] }>(url)
      .then(r => setFundPositionsCount(r?.positions?.length || 0))
      .catch(() => setFundPositionsCount(0));
  }, [pollingEnabled, clientId]);

  // React to API errors: 404 → redirect (manager); 409 → unlinked empty state.
  useEffect(() => {
    if (!error) { setErrorMsg(null); return; }
    const status = (error as { status?: number })?.status;
    if (status === 404 && user?.role !== 'client') {
      navigate('/fund', { replace: true });
      return;
    }
    if (status === 409) { setUnlinked(true); return; }
    setErrorMsg('Failed to load dashboard.');
  }, [error, user, navigate]);

  // Top-5 holdings with portfolio share. Must be called every render (hooks
  // rules) — so it lives above the early returns and tolerates null `data`.
  const holdings = data?.holdings ?? [];
  const aggregated = useMemo(() => {
    const total = holdings.reduce((s, h) => s + h.value, 0) || 1;
    return holdings.map(h => ({ ...h, pct: (h.value / total) * 100 }));
  }, [holdings]);

  if (unlinked) return <EmptyDashboard label="Your portfolio is being set up. Transactions and reports will appear here once trading begins." />;
  if (errorMsg) return (
    <div className="view container">
      <p style={{ fontFamily: 'var(--serif)', fontStyle: 'italic', color: 'var(--accent)' }}>{errorMsg}</p>
    </div>
  );
  if (!data) return (
    <div className="view container">
      <p style={{ fontFamily: 'var(--mono)', fontSize: 11, letterSpacing: '0.16em',
                  textTransform: 'uppercase', color: 'var(--ink-3)' }}>
        Loading…
      </p>
    </div>
  );

  const { client, period, totals, pending_deposit, activity, monthly, last_statement } = data;

  const closed = period?.status === 'closed';
  const flagged = period?.status === 'flagged';
  const asOfText = period?.as_of ? new Date(period.as_of).toISOString().slice(0, 16).replace('T', ' ') + ' UTC' : '—';
  const stale = totals.data_age_seconds != null && totals.data_age_seconds > totals.staleness_warn_seconds;
  const staleLabel = (() => {
    const s = totals.data_age_seconds ?? 0;
    if (s < 3600) return `${Math.round(s / 60)}m`;
    if (s < 86400) return `${Math.round(s / 3600)}h`;
    return `${Math.round(s / 86400)}d`;
  })();

  return (
    <div className="view container">
      {/* Phase 9.b — Fund-only investor pointer. Render whenever the    */}
      {/* investor holds units in any fund. Closes the audit gap where    */}
      {/* SMA-shaped Dashboard left Fund-only investors with no breadcrumb. */}
      {fundPositionsCount > 0 && (
        <div className="label-s" style={{
          marginBottom: 24, padding: '12px 16px',
          background: 'var(--paper-2)', border: 'var(--hairline)',
          borderColor: 'var(--rule)', display: 'flex',
          justifyContent: 'space-between', alignItems: 'center',
        }}>
          <span>YOU HOLD UNITS IN {fundPositionsCount === 1 ? '1 FUND' : `${fundPositionsCount} FUNDS`} · SMA ACCOUNTS BELOW DO NOT INCLUDE THIS POSITION</span>
          <a href={clientId ? `/fund?client_id=${clientId}` : '/fund'} style={{ color: 'var(--ink)' }}>
            VIEW FUND POSITION →
          </a>
        </div>
      )}
      {/* Page header */}
      <header className="dash-header" style={{
        display: 'grid', gridTemplateColumns: '1fr auto',
        alignItems: 'baseline', paddingBottom: 24,
        borderBottom: 'var(--hairline)', marginBottom: 56,
        gap: 32,
      }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <span className="page-kicker" style={{ margin: 0 }}>
            PORTFOLIO{period ? ` · ${period.label}` : ''}
          </span>
          <span className="serif" style={{ fontSize: 14, color: 'var(--ink-3)' }}>{client.legal_name}</span>
        </div>
        <div className="page-head-meta">
          <span className="label-s">
            AS OF {asOfText}
            {stale && (
              <span
                title={`Latest snapshot is ${staleLabel} old; warn threshold is ${Math.round(totals.staleness_warn_seconds / 60)}m. Background sync may have stalled.`}
                style={{
                  marginLeft: 10, padding: '1px 6px',
                  border: '0.5pt solid #6b2a2a', color: '#6b2a2a',
                  fontFamily: 'var(--mono)', fontSize: 9,
                  letterSpacing: '0.18em', textTransform: 'uppercase',
                }}
              >
                stale · {staleLabel}
              </span>
            )}
          </span>
          <span className="label-s" style={{ color: closed ? 'var(--ink-3)' : 'var(--accent)' }}>
            {closed ? 'CLOSED · STATEMENT ISSUED' : 'PERIOD IN PROGRESS'}
          </span>
        </div>
      </header>

      {/* Hero stat block */}
      <section className="dash-hero" style={{ padding: flagged ? '48px 0 64px' : '16px 0 96px' }}>
        <div className="stat-label" style={{ fontSize: 11, marginBottom: 24 }}>
          TOTAL VALUE
        </div>
        <div style={{ position: 'relative' }}>
          <DollarStat large label="" value={totals.aum} decimals={2} />
        </div>
        <div style={{
          marginTop: 32, paddingTop: 24, borderTop: 'var(--hairline)',
          display: 'grid', gridTemplateColumns: 'auto 1fr auto', gap: 32, alignItems: 'baseline',
        }}>
          <span className="label">{closed ? 'PERIOD RETURN' : 'MONTH-TO-DATE'}</span>
          <span className="num" style={{ fontSize: 24, color: totals.mtd_pct >= 0 ? 'var(--accent)' : 'var(--oxblood)' }}>
            {fmtPct(totals.mtd_pct)}
          </span>
          <span className="label-s">vs OPENING {fmtUSD(totals.aum_prev, 2)}</span>
        </div>
      </section>

      {/* Period status banner */}
      {period && (
        closed ? (
          <Banner
            kicker={`PERIOD · ${period.label} CLOSED`}
            body={<>This period is final. <em>Next snapshot opens at the start of next period.</em></>}
            action={<button className="btn-link" onClick={() => goto('/statements')}>View statement →</button>}
          />
        ) : (
          <Banner
            kicker="PERIOD · IN PROGRESS"
            body={<><em>Closes {fmtPrettyDate(period.closes)}.</em>{period.statement_available ? <> Statement available approximately {fmtPrettyDate(period.statement_available)}.</> : null}</>}
            action={<button className="btn-link" onClick={() => goto('/statements')}>Past statements →</button>}
          />
        )
      )}

      {/* Pending cash flow */}
      {pending_deposit && (
        <div style={{
          marginTop: 24, padding: '20px 0', borderBottom: 'var(--hairline)',
          display: 'grid', gridTemplateColumns: '220px 1fr auto', gap: 32, alignItems: 'baseline',
        }}>
          <div className="banner-kicker" style={{ color: 'var(--ink-3)' }}>
            PENDING · WILL APPLY {fmtPrettyDate(pending_deposit.applies_on).toUpperCase()}
          </div>
          <div className="serif-body" style={{ margin: 0 }}>
            Pending deposit of <em>{fmtUSD(pending_deposit.amount, 0)}</em>{pending_deposit.source ? <> from {pending_deposit.source}</> : null} —
            applied at the start of the next period.
          </div>
          <span className="label-s">RECEIVED {fmtPrettyDate(pending_deposit.received)}</span>
        </div>
      )}

      {/* Secondary stats strip */}
      <section className="dash-strip" style={{ marginTop: 56 }}>
        <div className="agg-strip" style={{ ['--cols' as any]: 4 } as any}>
          <div className="agg-cell">
            <Stat label="INCEPTION-TO-DATE RETURN"
                  value={<span className={totals.itd_pct >= 0 ? 'pos' : 'neg'}>{fmtPct(totals.itd_pct)}</span>}
                  sub={`SINCE ${new Date(client.account_opened).toLocaleDateString('en-US', { month: 'short', year: 'numeric' }).toUpperCase()}`} />
          </div>
          <div className="agg-cell">
            <Stat label="YEAR-TO-DATE RETURN"
                  value={<span className={totals.ytd_pct >= 0 ? 'pos' : 'neg'}>{fmtPct(totals.ytd_pct)}</span>}
                  sub={period?.short_label?.split(' ')[1] || ''} />
          </div>
          <div className="agg-cell">
            <Stat label="BENCHMARK · BTC ITD"
                  value={<span className="num" style={{ color: totals.benchmark_available ? undefined : 'var(--ink-3)' }}>
                    {totals.benchmark_available ? fmtPct(totals.btc_itd_pct) : '—'}
                  </span>}
                  sub={totals.benchmark_available ? `BTC MTD · ${fmtPct(totals.btc_mtd_pct)}` : 'BENCHMARK PENDING'} />
          </div>
          <div className="agg-cell">
            <Stat label="UNREALIZED P&L"
                  value={<span className={totals.unrealized >= 0 ? 'pos' : 'neg'}>{fmtUSDcompact(totals.unrealized)}</span>}
                  sub={`COST BASIS ${fmtUSDcompact(totals.cost_basis)}`} />
          </div>
        </div>
      </section>

      {/* Sparkline — only if we have closed periods to chart */}
      {monthly.length >= 2 && (
        <section className="clean-hide" style={{ marginTop: 64 }}>
          <KickerRow kicker="VALUE OVER TIME · INCEPTION-TO-DATE"
                     action="View performance →"
                     onAction={() => goto('/performance')} />
          <Sparkline series={monthly.map(m => ({ x: m.month, y: m.ending }))} height={220} />
        </section>
      )}

      {/* Holdings + recent activity — stacked in editorial, side-by-side
          internally-scrolling panels in clean mode (clean-cols is inert in
          editorial so the layout is unchanged there). */}
      <div className="clean-cols dash-cols">
      {/* Top holdings panel */}
      {aggregated.length > 0 && (
        <section className="clean-panel" style={{ marginTop: 96 }}>
          <KickerRow kicker={clean ? 'HOLDINGS' : 'HOLDINGS · TOP FIVE'}
                     action="View all holdings →"
                     onAction={() => goto('/holdings')} />
          <table className="tbl">
            <thead>
              <tr>
                <th>ASSET</th>
                <th data-align="right">QUANTITY</th>
                <th data-align="right">MARKET VALUE</th>
                <th data-align="right">% OF PORTFOLIO</th>
                <th data-align="right">UNREALIZED P&L</th>
              </tr>
            </thead>
            <tbody>
              {(clean ? aggregated : aggregated.slice(0, 5)).map(h => {
                const pl = h.value - h.cost_basis;
                const plPct = h.cost_basis !== 0 ? (pl / h.cost_basis) * 100 : 0;
                const isStable = h.asset === 'USDC' || h.asset === 'USDT' || h.asset === 'USD';
                return (
                  <tr key={h.asset}>
                    <td>
                      <div className="asset">
                        <AssetMark asset={h.asset} />
                        <div>
                          <div className="asset-name">{ASSET_NAMES[h.asset] || h.asset}</div>
                          <div className="asset-ticker">{h.asset}</div>
                        </div>
                      </div>
                    </td>
                    <td data-align="right" className="num">
                      {fmtQty(h.qty, isStable ? 2 : 4)}
                    </td>
                    <td data-align="right" className="num">{fmtUSD(h.value)}</td>
                    <td data-align="right" className="num">{h.pct.toFixed(2)}%</td>
                    <td data-align="right" className="num"
                        style={{ color: pl >= 0 ? 'var(--accent)' : 'var(--oxblood)' }}>
                      {fmtPct(plPct)}
                      <div className="label-s" style={{ marginTop: 2, color: 'var(--ink-3)' }}>
                        {pl >= 0 ? '+' : '−'}{fmtUSDcompact(Math.abs(pl))}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </section>
      )}

      {/* Recent activity */}
      {activity.length > 0 && (
        <section className="clean-panel" style={{ marginTop: 96 }}>
          <KickerRow kicker="RECENT ACTIVITY"
                     action="View all activity →"
                     onAction={() => goto('/activity')} />
          <table className="tbl">
            <thead>
              <tr>
                <th style={{ width: 180 }}>DATE / TIME</th>
                <th>TYPE</th>
                <th>ASSET</th>
                <th data-align="right">QUANTITY</th>
                <th data-align="right">USD VALUE</th>
                <th>REFERENCE</th>
              </tr>
            </thead>
            <tbody>
              {activity.map(a => {
                const isStable = a.asset === 'USDC' || a.asset === 'USDT' || a.asset === 'USD';
                return (
                  <tr key={a.venue_tx_id || `${a.occurred_at}-${a.asset}`}>
                    <td className="num" style={{ color: 'var(--ink-2)', fontSize: 12 }}>
                      {new Date(a.occurred_at).toISOString().slice(0, 16).replace('T', ' ')}
                    </td>
                    <td><TypePill type={uiTxType(a.tx_type)} /></td>
                    <td><span className="serif" style={{ fontSize: 16 }}>{a.asset}</span></td>
                    <td data-align="right" className="num">{fmtQty(a.quantity, isStable ? 2 : 4)}</td>
                    <td data-align="right" className="num">{fmtUSD(a.usd_value)}</td>
                    <td className="num" style={{ color: 'var(--ink-3)', fontSize: 12 }}>{a.venue_tx_id || '—'}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </section>
      )}
      </div>{/* /clean-cols */}

      {/* Latest statement */}
      {last_statement && (
        <section className="clean-hide" style={{
          marginTop: 96, padding: '32px 0',
          borderTop: '1px solid var(--ink)', borderBottom: '1px solid var(--ink)',
          display: 'grid', gridTemplateColumns: '1fr auto', gap: 32, alignItems: 'center',
        }}>
          <div>
            <div className="label" style={{ marginBottom: 14 }}>LATEST STATEMENT</div>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 24, flexWrap: 'wrap' }}>
              <span className="display-m">{last_statement.period_label}</span>
              <span className="label-s">ISSUED {last_statement.issued_at ? fmtPrettyDate(last_statement.issued_at).toUpperCase() : '—'}</span>
              <span className="label-s">V{last_statement.version}</span>
            </div>
          </div>
          <div style={{ display: 'flex', gap: 16 }}>
            <button className="btn-ghost btn" onClick={() => goto(`/statements/${last_statement.id}`)}>View online</button>
          </div>
        </section>
      )}

      {/* Methodology footnote */}
      <section className="clean-hide" style={{ marginTop: 64, paddingBottom: 24 }}>
        <div style={{ maxWidth: '76ch' }}>
          <div className="label" style={{ marginBottom: 12 }}>METHODOLOGY</div>
          <p className="serif-body" style={{ margin: 0, fontSize: 16 }}>
            Period and inception returns are computed on a time-weighted basis (Modified Dietz), weighting each cash
            flow by the fraction of the period it was invested; issued statements also present net-of-fee returns.
            Cost basis is computed FIFO from transaction history. Mark-to-market via venue spot prices at snapshot
            time. The BTC benchmark is unmanaged and shown for reference only. Past performance does not guarantee
            future results.
          </p>
        </div>
      </section>
    </div>
  );
}

// "trade_buy" → "buy" so the existing TypePill mapping renders the right pill.
function uiTxType(serverTxType: string): string {
  if (serverTxType === 'trade_buy') return 'buy';
  if (serverTxType === 'trade_sell') return 'sell';
  if (serverTxType === 'transfer_in' || serverTxType === 'transfer_out') return 'transfer';
  if (serverTxType === 'adjustment') return 'rebalance';
  return serverTxType;
}

function fmtPrettyDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
}

// ─── Sparkline ─────────────────────────────────────────────────────────────
function Sparkline({ series, height = 200 }: { series: { x: string; y: number }[]; height?: number }) {
  const W = 1200, H = height;
  const padL = 0, padR = 60, padT = 16, padB = 28;
  const xs = series.map((_, i) => padL + (i / (series.length - 1)) * (W - padL - padR));
  const ys = series.map(p => p.y);
  const yMin = Math.min(...ys), yMax = Math.max(...ys);
  const yRange = yMax - yMin || 1;
  const ny = ys.map(y => padT + (1 - (y - yMin) / yRange) * (H - padT - padB));
  const path = xs.map((x, i) => `${i === 0 ? 'M' : 'L'} ${x.toFixed(2)} ${ny[i]!.toFixed(2)}`).join(' ');

  const [hover, setHover] = useState<number | null>(null);
  const onMove = (e: any) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const xRel = ((e.clientX - rect.left) / rect.width) * W;
    let nearest = 0, minDist = Infinity;
    xs.forEach((x, i) => {
      const d = Math.abs(x - xRel);
      if (d < minDist) { minDist = d; nearest = i; }
    });
    setHover(nearest);
  };

  return (
    <div style={{ width: '100%', position: 'relative' }} onMouseLeave={() => setHover(null)}>
      <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none"
           style={{ width: '100%', height, display: 'block' }}
           onMouseMove={onMove}>
        {[0, 0.5, 1].map(t => {
          const y = padT + t * (H - padT - padB);
          return <line key={t} x1={padL} x2={W - padR} y1={y} y2={y} stroke="var(--rule-soft)" strokeWidth="0.5" />;
        })}
        {[1, 0.5, 0].map(t => {
          const y = padT + t * (H - padT - padB);
          const v = yMin + (1 - t) * yRange;
          return (
            <text key={t} x={W - padR + 8} y={y + 4}
                  fontFamily="var(--mono)" fontSize="10" fill="var(--ink-3)" letterSpacing="0.05em">
              {fmtUSDcompact(v)}
            </text>
          );
        })}
        <path d={path} fill="none" stroke="var(--accent)" strokeWidth="1.25" />
        {hover != null && (
          <>
            <line x1={xs[hover]!} x2={xs[hover]!} y1={padT} y2={H - padB}
                  stroke="var(--ink)" strokeWidth="0.5" strokeDasharray="2 3" />
            <circle cx={xs[hover]!} cy={ny[hover]!} r="3" fill="var(--accent)" />
          </>
        )}
        {[0, Math.floor(series.length / 2), series.length - 1].map(i => (
          <text key={i} x={xs[i]!} y={H - 8}
                fontFamily="var(--mono)" fontSize="10" fill="var(--ink-3)" letterSpacing="0.05em"
                textAnchor={i === 0 ? 'start' : i === series.length - 1 ? 'end' : 'middle'}>
            {series[i]!.x.toUpperCase()}
          </text>
        ))}
      </svg>
      {hover != null && (
        <div style={{
          position: 'absolute', top: 8, left: `${(xs[hover]! / W) * 100}%`,
          transform: 'translateX(-50%)',
          background: 'var(--paper)', border: '1px solid var(--ink)',
          padding: '6px 12px', whiteSpace: 'nowrap',
          fontFamily: 'var(--mono)', fontSize: 11, letterSpacing: '0.04em',
          pointerEvents: 'none',
        }}>
          <div className="label-s" style={{ marginBottom: 2, color: 'var(--ink-3)' }}>
            {series[hover]!.x.toUpperCase()}
          </div>
          <div className="num" style={{ color: 'var(--ink)', fontSize: 13 }}>
            {fmtUSD(series[hover]!.y, 0)}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Empty state ───────────────────────────────────────────────────────────
function EmptyDashboard({ label }: { label: string }) {
  const navigate = useNavigate();
  return (
    <div className="view container" style={{ paddingTop: 96 }}>
      <Kicker>PORTFOLIO</Kicker>
      <div style={{ marginTop: 80, paddingTop: 64, borderTop: '1px solid var(--ink)' }}>
        <div className="label" style={{ marginBottom: 24 }}>ACCOUNT · AWAITING FIRST EXECUTION</div>
        <p className="serif-body" style={{ fontSize: 24, maxWidth: '46ch', margin: 0 }}>
          <em>{label}</em>
        </p>
        <div style={{
          marginTop: 64, paddingTop: 32, borderTop: 'var(--hairline)',
          display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 32,
        }}>
          <div>
            <div className="label" style={{ marginBottom: 12 }}>CONTACT</div>
            <p className="serif-body" style={{ fontSize: 16, margin: 0 }}>
              Questions on onboarding?{' '}
              <button className="btn-link" onClick={() => navigate('/portal/contact')}>Reach the desk →</button>
            </p>
          </div>
          <div>
            <div className="label" style={{ marginBottom: 12 }}>DOCUMENTS</div>
            <p className="serif-body" style={{ fontSize: 16, margin: 0 }}>
              <button className="btn-link" onClick={() => navigate('/documents')}>Review agreements →</button>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
